import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import { APPLICATION_STATUS } from '../utils/constants';
import { getUserApplications } from '../services/firestore';
import useApplicationStore from '../stores/applicationStore';
import useAuthStore from '../stores/authStore';

const Dashboard = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, profile } = useAuthStore();
  const { setApplications: setStoreApplications } = useApplicationStore();

  useEffect(() => {
    const fetchApplications = async () => {
      if (user?.uid) {
        setLoading(true);
        try {
          const result = await getUserApplications(user.uid);
          if (result.success) {
            setApplications(result.applications);
            setStoreApplications(result.applications);
          } else {
            console.error('Error fetching applications:', result.error);
          }
        } catch (error) {
          console.error('Error fetching applications:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchApplications();
  }, [user, setStoreApplications]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Processing' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    let date;
    
    // Handle Firestore Timestamp objects
    if (timestamp && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } 
    // Handle Firestore Timestamp objects that come as plain objects with seconds/nanoseconds
    else if (timestamp && timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
    }
    // Handle regular Date objects or ISO strings
    else if (timestamp && timestamp !== null && typeof timestamp === 'string') {
      date = new Date(timestamp);
    } 
    // Handle invalid/missing timestamps or serverTimestamp placeholders
    else {
      return 'N/A';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatApplicationId = (id) => {
    return id ? id.slice(-8).toUpperCase() : 'N/A';
  };

  const handleNewApplication = () => {
    navigate('/service-fee-selection');
  };

  const getApplicationStats = () => {
    const total = applications.length;
    const pending = applications.filter(app => app.status === 'pending').length;
    const approved = applications.filter(app => app.status === 'approved').length;
    const rejected = applications.filter(app => app.status === 'rejected').length;
    
    return { total, pending, approved, rejected };
  };

  const stats = getApplicationStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
              <span className="text-white text-xl font-bold">
                {profile?.fullName?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                Welcome back, {profile?.fullName?.split(' ')[0] || 'User'}!
              </h1>
              <p className="text-primary-100">
                Manage your loans and applications
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Applications</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
            <div className="text-sm text-gray-600">Approved</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
            <div className="text-sm text-gray-600">Rejected</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Button
              onClick={handleNewApplication}
              size="lg"
              className="w-full"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Apply for New Loan
            </Button>
            <Button
              onClick={() => navigate('/loan-limits')}
              variant="outline"
              size="lg"
              className="w-full"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Check Loan Limits
            </Button>
          </div>
        </div>

        {/* Profile Summary */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Summary</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-500">M-Pesa Number</div>
              <div className="font-medium">{profile?.mpesaNumber}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">County</div>
              <div className="font-medium">{profile?.county}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Monthly Income</div>
              <div className="font-medium">KES {profile?.monthlyIncome?.toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* Applications List */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Recent Applications</h2>
            <span className="text-sm text-gray-500">{applications.length} total</span>
          </div>

          {applications.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No applications yet</h3>
              <p className="text-gray-500 mb-6">Apply for your first loan to get started</p>
              <Button onClick={handleNewApplication}>
                Apply for Your First Loan
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => (
                <div key={app.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-lg font-semibold text-gray-900">
                          KES {app.eligibleLoanAmount?.toLocaleString()}
                        </span>
                        {getStatusBadge(app.status)}
                      </div>
                      <p className="text-sm text-gray-600">
                        Purpose: {app.loanPurpose || 'Personal'}
                      </p>
                    </div>
                    <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {formatApplicationId(app.id)}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Service Fee:</span>
                      <p className="font-medium">KES {app.serviceAmount}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Applied:</span>
                      <p className="font-medium">{formatDate(app.appliedAt)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">M-Pesa Number:</span>
                      <p className="font-mono text-xs">{app.mpesaNumber}</p>
                    </div>
                  </div>

                  {app.reviewedAt && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-sm">
                        <span className="text-gray-500">Reviewed:</span>
                        <span className="font-medium ml-1">{formatDate(app.reviewedAt)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;